'use client';

import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Tooltip } from '@repo/design-system/components/ds/tooltip/tooltip';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectMapId } from '@repo/store/maps/slices/mapData';
import { useEffect, useMemo, useState } from 'react';
import { useMapControls } from '../game-map/hooks/useMapControls';
import { useMapContext } from '../game-map/map-context';
const DEGREES_EPSILON = 0.5;
const PITCH_EPSILON = 0.5;
export const ResetOrientationButton = () => {
  const $ = _c(26);
  const {
    map
  } = useMapContext();
  const {
    getBearing,
    getPitch,
    resetOrientation
  } = useMapControls();
  const [showReset, setShowReset] = useState(false);
  const analytics = useAnalytics();
  const mapId = useAppSelector(selectMapId) ?? "";
  const hostType = useHostType();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["game_map"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useT(t0);
  let t1;
  if ($[1] !== getBearing || $[2] !== getPitch) {
    t1 = () => {
      const bearing = getBearing();
      const pitch = getPitch();
      const normalizedBearing = (bearing % 360 + 360) % 360;
      const deltaFromNorth = Math.min(normalizedBearing, 360 - normalizedBearing);
      return deltaFromNorth < DEGREES_EPSILON && Math.abs(pitch) < PITCH_EPSILON;
    };
    $[1] = getBearing;
    $[2] = getPitch;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isDefaultOrientation = t1;
  let t2;
  let t3;
  if ($[4] !== isDefaultOrientation || $[5] !== map) {
    t2 = () => {
      if (!map) {
        return;
      }
      const handleChange = () => {
        setShowReset(!isDefaultOrientation());
      };
      handleChange();
      map.on("move", handleChange);
      map.on("rotate", handleChange);
      map.on("pitch", handleChange);
      return () => {
        map.off("move", handleChange);
        map.off("rotate", handleChange);
        map.off("pitch", handleChange);
      };
    };
    t3 = [map, isDefaultOrientation];
    $[4] = isDefaultOrientation;
    $[5] = map;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== analytics || $[9] !== hostType || $[10] !== mapId || $[11] !== resetOrientation) {
    t4 = () => {
      resetOrientation();
      if (analytics && mapId) {
        analytics.event("game_map_reset_orientation_click", {
          mapId,
          platform: hostType
        });
      }
    };
    $[8] = analytics;
    $[9] = hostType;
    $[10] = mapId;
    $[11] = resetOrientation;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const onClick = t4;
  if (!showReset) {
    return null;
  }
  let t5;
  if ($[13] !== t) {
    t5 = t(_temp);
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t) {
    t6 = t(_temp2);
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = css({
      borderRadius: "full",
      "&:not(:focus-visible)": {
        boxShadow: "lg"
      }
    });
    t8 = <Icon icon="recenter" decorative={true} />;
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  let t9;
  if ($[19] !== onClick || $[20] !== t6 || $[21] !== t7) {
    t9 = <IconButton onClick={onClick} color="white" size="lg" aria-label={t6} className={t7}>{t8}</IconButton>;
    $[19] = onClick;
    $[20] = t6;
    $[21] = t7;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t5 || $[24] !== t9) {
    t10 = <Tooltip content={t5} offset={8} placement="top" size="md">{t9}</Tooltip>;
    $[23] = t5;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};
function _temp($) {
  return $.reset_orientation;
}
function _temp2($_0) {
  return $_0.reset_orientation;
}